import { CalendarRange, ChevronRight } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import DateFilterPanel, { MonthYearPicker } from "./DateFilterPanel"

interface DateRangeCardProps {
  /** The earliest date available in the dataset (year decimal, e.g. 2005.5). */
  minDate: number
  /** The latest date available in the dataset (year decimal). */
  maxDate: number
  /** The currently selected start of the date range (year decimal). */
  rangeStart: number
  /** The currently selected end of the date range (year decimal). */
  rangeEnd: number

  onRangeStartChange: (value: number) => void
  onRangeEndChange: (value: number) => void
  alignPivotToPx?: boolean
}

export default function DateRangeCard({
  minDate,
  maxDate,
  rangeStart,
  rangeEnd,
  onRangeStartChange,
  onRangeEndChange,
  alignPivotToPx = true,
}: DateRangeCardProps): React.JSX.Element {
  return (
    <Collapsible
      defaultOpen
      aria-label="Date range filter"
      className="overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md"
    >
      <div className="flex w-full flex-col gap-2 px-4 py-2 lg:flex-row lg:items-center">
        <CollapsibleTrigger className="group flex flex-1 items-center gap-2 text-left transition-colors hover:text-foreground">
          <CalendarRange className="size-3.5 text-muted-foreground -mt-0.5" aria-hidden="true" />
          <span className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
            Date range
          </span>
          <ChevronRight className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
        </CollapsibleTrigger>

        {/* Start/End stay inline with each other; the whole group drops to its
            own line below the trigger on small screens. */}
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] text-muted-foreground">Start</span>
          <MonthYearPicker
            label="Start"
            value={rangeStart}
            min={minDate}
            max={rangeEnd}
            onChange={onRangeStartChange}
          />
          <span className="text-[11px] text-muted-foreground" aria-hidden="true">
            –
          </span>
          <span className="text-[11px] text-muted-foreground">End</span>
          <MonthYearPicker
            label="End"
            value={rangeEnd}
            min={rangeStart}
            max={maxDate}
            onChange={onRangeEndChange}
          />
        </div>
      </div>

      <CollapsibleContent>
        <div className="border-t border-border/60 px-5 pb-4 pt-4">
          <DateFilterPanel
            minDate={minDate}
            maxDate={maxDate}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onRangeStartChange={onRangeStartChange}
            onRangeEndChange={onRangeEndChange}
            alignPivotToPx={alignPivotToPx}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
